import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { track } from "@/lib/api";
import {
  ArrowLeft,
  ArrowRight,
  UsersThree,
  HandsPraying,
  Quotes,
  PaperPlaneTilt,
  Sparkle,
  CheckCircle,
  Flag,
  SignOut,
  Lock,
  HeartStraight,
  ShieldCheck,
} from "@phosphor-icons/react";

const QUICK_PRAYERS = [
  "Praying for peace and wisdom.",
  "May God strengthen and comfort you.",
  "Lord, bring clarity and hope.",
];

const STEP_LABELS = ["Opening", "Focus", "Reflect", "Pray", "Closing"];

function StepIndicator({ current, total }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="session-progress">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all ${
            i === current ? "w-7 bg-[#C4A265]" : i < current ? "w-3 bg-[#C4A265]/50" : "w-3 bg-[#D4C9BA]"
          }`}
        />
      ))}
      <span className="text-[#8B7355] text-[10px] font-body ml-2">
        Step {Math.min(current + 1, total)} of {total}
      </span>
    </div>
  );
}

export default function CircleDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftType, setDraftType] = useState("intent");
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/circles/${slug}`);
      setData(r.data);
      if (r.data.circle?.is_member) {
        const m = await api.get(`/circles/${slug}/messages`);
        setMessages(m.data.items || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load circle");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleJoin = async () => {
    setBusy(true);
    try {
      await api.post(`/circles/${slug}/join`);
      track("circle_joined", { slug });
      toast.success("You're in. Welcome.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join");
    } finally {
      setBusy(false);
    }
  };

  const handleLeave = async () => {
    setBusy(true);
    try {
      await api.post(`/circles/${slug}/leave`);
      track("circle_left", { slug });
      toast("You've left this circle.");
      navigate("/community");
    } catch {
      toast.error("Couldn't leave");
    } finally {
      setBusy(false);
    }
  };

  const startSession = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/circles/${slug}/sessions`);
      track("circle_session_started", { slug, session_id: r.data.id });
      toast.success("Session started");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start");
    } finally {
      setBusy(false);
    }
  };

  const advance = async () => {
    if (!data?.active_session) return;
    setBusy(true);
    try {
      const r = await api.post(`/circles/${slug}/sessions/${data.active_session.id}/advance`);
      track("circle_session_step_advanced", { slug, step: r.data.session.current_step });
      if (r.data.completed) {
        track("circle_session_completed", { slug });
        toast.success("Session complete. Amen.");
      }
      load();
    } catch {
      toast.error("Couldn't advance");
    } finally {
      setBusy(false);
    }
  };

  const sendMsg = async (overrideContent, inResponseTo) => {
    const raw = typeof overrideContent === "string" ? overrideContent : draft;
    const content = (raw ?? "").trim();
    if (!content) return;
    setBusy(true);
    try {
      await api.post(`/circles/${slug}/messages`, {
        type: draftType,
        content,
        session_id: data?.active_session?.id || undefined,
        in_response_to: inResponseTo || undefined,
      });
      track("circle_message_posted", { slug, type: draftType, in_response_to: !!inResponseTo });
      if (inResponseTo) track("circle_prayer_response_sent", { slug });
      setDraft("");
      const m = await api.get(`/circles/${slug}/messages`);
      setMessages(m.data.items || []);
    } catch {
      toast.error("Couldn't share");
    } finally {
      setBusy(false);
    }
  };

  const flagCircle = async () => {
    const reason = window.prompt("What concern would you like the team to review?");
    if (!reason) return;
    try {
      await api.post(`/circles/${slug}/flag`, { scope: "circle", target_id: data.circle.id, reason });
      track("circle_flagged", { slug });
      toast("Thank you — our team will review.");
    } catch {
      toast.error("Couldn't submit flag");
    }
  };

  if (loading) {
    return (
      <div className="px-5 md:px-8 py-10 max-w-3xl mx-auto text-center text-[#8B7355] font-body text-sm">
        Loading circle…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="px-5 md:px-8 py-10 max-w-3xl mx-auto text-center" data-testid="circle-not-found">
        <p className="text-[#5C4033] font-body text-sm mb-4">Circle not found.</p>
        <Link to="/community" className="text-[#C4A265] underline">Back to community</Link>
      </div>
    );
  }

  const c = data.circle;
  const tpl = data.template;
  const session = data.active_session;
  const stepIdx = session?.current_step ?? 0;
  const stepKey = data.session_steps?.[stepIdx]?.key;
  const stepTitle = data.session_steps?.[stepIdx]?.title;

  const stepContent = (() => {
    if (!session || !tpl) return null;
    if (stepKey === "opening_scripture") return tpl.opening_scripture;
    if (stepKey === "focus")             return tpl.focus;
    if (stepKey === "reflection")        return tpl.reflection_prompt;
    if (stepKey === "shared_prayer")     return tpl.shared_prompt;
    if (stepKey === "closing")           return tpl.closing;
    return null;
  })();

  return (
    <div className="px-5 md:px-8 py-4 max-w-3xl mx-auto" data-testid="circle-detail-page">
      <Link to="/community" className="inline-flex items-center gap-1 text-[#8B7355] text-xs font-body mb-4">
        <ArrowLeft size={14} /> Back to circles
      </Link>

      {/* Header */}
      <div className="glass-card-strong p-6 mb-5 relative overflow-hidden" data-testid="circle-header">
        <div className="spiritual-orb-sm w-40 h-40 -top-12 -right-10 animate-orb opacity-40" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium">
              {c.topic.replace("-", " ")}
            </p>
            <span className="text-[#D4C9BA]">·</span>
            <span className={`text-[10px] uppercase tracking-wider font-body ${
              c.status === "open" ? "text-emerald-700" : c.status === "full" ? "text-amber-700" : "text-[#8B7355]"
            }`}>
              {c.status}
            </span>
            {c.visibility === "private" && <Lock size={11} className="text-[#8B7355]" />}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] font-medium mb-2">{c.name}</h1>
          <p className="text-[#5C4033] font-body text-sm leading-relaxed mb-4">{c.description}</p>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="inline-flex items-center gap-2 text-[#8B7355] text-xs font-body">
              <UsersThree size={14} weight="duotone" className="text-[#C4A265]" />
              {c.member_count}/{c.capacity} walking together
            </div>
            {c.is_member ? (
              <div className="inline-flex gap-2">
                <button
                  onClick={flagCircle}
                  className="text-[#8B7355] text-xs font-body inline-flex items-center gap-1 hover:text-[#5C4033]"
                  data-testid="flag-btn"
                >
                  <Flag size={12} /> Flag
                </button>
                <button
                  onClick={handleLeave}
                  disabled={busy}
                  data-testid="leave-btn"
                  className="px-3 py-1.5 rounded-full glass-card text-[#5C4033] text-xs font-body inline-flex items-center gap-1 hover:bg-white/60"
                >
                  <SignOut size={12} /> Leave
                </button>
              </div>
            ) : c.status === "full" ? (
              <button disabled className="px-5 py-2 rounded-full bg-[#D4C9BA] text-white text-xs font-body opacity-70" data-testid="join-btn-full">Full</button>
            ) : (
              <button
                onClick={handleJoin}
                disabled={busy}
                data-testid="join-btn"
                className="px-5 py-2 rounded-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white text-xs font-body font-medium inline-flex items-center gap-1 disabled:opacity-60"
              >
                Join Circle <ArrowRight size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Members */}
      {c.is_member && (
        <div className="glass-card p-4 mb-5" data-testid="member-list">
          <p className="text-[#8B7355] text-[10px] uppercase tracking-wider font-body mb-2">In this circle</p>
          <div className="flex flex-wrap gap-2">
            {data.members.map((m) => (
              <div key={m.user_id} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FAF5EC] border border-[#E8DFD3]">
                <div className="w-6 h-6 rounded-full bg-[#E5D9C7] flex items-center justify-center text-[10px] font-heading text-[#5C4033]">
                  {(m.user_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <span className="text-xs font-body text-[#5C4033]">{m.user_name || "Friend"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guided session */}
      {c.is_member && (
        <div className="glass-card-strong p-6 mb-5" data-testid="session-panel">
          <div className="flex items-center gap-2 mb-3">
            <HandsPraying size={18} weight="duotone" className="text-[#C4A265]" />
            <h2 className="font-heading text-lg text-[#2D1B0E] font-medium">{tpl?.name || "Guided Group Prayer"}</h2>
          </div>

          {!session ? (
            <div className="text-center py-4">
              <p className="text-[#5C4033] font-body text-sm mb-4 max-w-md mx-auto">
                A 5-step guided prayer—opening Scripture, prayer focus, quiet reflection,
                shared prayer for one another, and a closing.
              </p>
              <button
                onClick={startSession}
                disabled={busy}
                data-testid="start-session-btn"
                className="px-6 py-2.5 rounded-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white text-sm font-body font-medium inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Sparkle size={14} weight="fill" /> Begin a Group Prayer
              </button>
            </div>
          ) : session.status === "completed" ? (
            <div className="text-center py-3" data-testid="session-completed">
              <CheckCircle size={28} weight="fill" className="text-emerald-600 mx-auto mb-2" />
              <p className="font-heading text-base text-[#2D1B0E] mb-1">This circle's session is complete.</p>
              <p className="text-[#8B7355] font-body text-xs">Begin a new one when you're ready.</p>
              <button
                onClick={startSession}
                disabled={busy}
                className="mt-4 px-5 py-2 rounded-full glass-card text-[#5C4033] text-xs font-body font-medium hover:bg-white/60"
                data-testid="start-new-session-btn"
              >
                Start a new session
              </button>
            </div>
          ) : (
            <div data-testid="session-active">
              <div className="mb-4">
                <StepIndicator current={stepIdx} total={data.session_steps.length} />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {STEP_LABELS.map((l, i) => (
                    <span key={i} className={`text-[10px] font-body px-2 py-0.5 rounded-full ${i === stepIdx ? "bg-[#FAF5EC] text-[#5C4033] font-medium" : "text-[#B8A088]"}`}>
                      {l}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-white/60 border border-[#E8DFD3] rounded-xl p-4 mb-3">
                <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.2em] font-body font-medium mb-1.5">
                  {stepTitle}
                </p>
                {stepKey === "opening_scripture" || stepKey === "closing" ? (
                  <>
                    <Quotes size={14} weight="fill" className="text-[#C4A265] mb-1" />
                    <p className="font-heading text-base italic text-[#2D1B0E] leading-relaxed">{stepContent}</p>
                  </>
                ) : (
                  <p className="font-body text-sm text-[#5C4033] leading-relaxed">{stepContent}</p>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  onClick={advance}
                  disabled={busy}
                  data-testid="advance-step-btn"
                  className="px-5 py-2 rounded-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white text-xs font-body font-medium inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {stepIdx >= data.session_steps.length - 1 ? "Amen — Finish" : "Continue"}
                  <ArrowRight size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Message feed */}
      {c.is_member && (
        <div className="glass-card p-5 mb-5" data-testid="message-feed">
          <p className="text-[#8B7355] text-[10px] uppercase tracking-wider font-body mb-3">Shared with the circle</p>
          {messages.length === 0 ? (
            <p className="text-center text-[#B8A088] font-body text-xs italic py-3">
              No intents shared yet. Be the first to drop one.
            </p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="messages-list">
              {messages.map((m) => {
                if (m.type === "prayer_share") {
                  return <PrayerShareCard key={m.id} m={m} onPray={(content) => sendMsg(content, m.id)} onPrayerSupported={() => track("circle_prayer_supported", { slug, msg_id: m.id })} />;
                }
                return (
                  <div
                    key={m.id}
                    data-testid={`msg-${m.type}`}
                    className={`px-3 py-2 rounded-lg text-sm font-body ${
                      m.type === "system"
                        ? "bg-[#FAF5EC] border border-[#E8DFD3] italic text-[#5C4033]"
                        : m.type === "intent"
                        ? "bg-white/70 border border-[#E8DFD3] text-[#2D1B0E]"
                        : m.type === "prayer"
                        ? "bg-emerald-50 border border-emerald-100 text-[#2D1B0E]"
                        : "bg-amber-50 border border-amber-100 text-[#2D1B0E]"
                    }`}
                  >
                    <div className="flex items-baseline justify-between mb-1">
                      <span className={`text-[10px] font-medium uppercase tracking-wider ${
                        m.type === "system" ? "text-[#C4A265]" : "text-[#8B7355]"
                      }`}>
                        {m.user_name || "Friend"} · {m.type}
                      </span>
                      <span className="text-[10px] text-[#B8A088]">
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                      </span>
                    </div>
                    <p className="leading-relaxed">{m.content}</p>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {["intent", "prayer", "encouragement"].map((t) => (
              <button
                key={t}
                onClick={() => setDraftType(t)}
                data-testid={`type-${t}`}
                className={`px-3 py-1 rounded-full text-[11px] font-body capitalize ${
                  draftType === t ? "bg-[#2D1B0E] text-white" : "glass-card text-[#5C4033] hover:bg-white/60"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMsg()}
              data-testid="message-input"
              placeholder={
                draftType === "intent"     ? "Briefly name what you're carrying…"
                : draftType === "prayer"   ? "Pray a short prayer for someone above…"
                                           : "Encourage with a short word…"
              }
              className="flex-1 px-4 py-2.5 rounded-full bg-white/70 border border-[#E5D9C7] font-body text-sm text-[#2D1B0E] focus:outline-none focus:border-[#C4A265]"
            />
            <button
              onClick={sendMsg}
              disabled={busy || !draft.trim()}
              data-testid="send-btn"
              className="px-4 py-2.5 rounded-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white text-xs font-body font-medium inline-flex items-center gap-1 disabled:opacity-50"
            >
              <PaperPlaneTilt size={13} /> Share
            </button>
          </div>
          <p className="text-[10px] text-[#B8A088] font-body mt-2 italic">
            Scripture-centered. No politics, no doctrine wars. Anything off-tone can be flagged.
          </p>
        </div>
      )}
    </div>
  );
}

const QUICK_PRAYER_PROMPTS = [
  "Praying for peace and wisdom.",
  "May God strengthen and comfort you.",
  "Lord, bring clarity and hope.",
];

function PrayerShareCard({ m, onPray, onPrayerSupported }) {
  const [sent, setSent] = useState(false);
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");

  // Fire view event once per mount.
  useEffect(() => {
    track("circle_prayer_intent_viewed", { msg_id: m.id });
    // eslint-disable-next-line
  }, []);

  const handleQuick = async (text) => {
    await onPray(text);
    setSent(true);
    onPrayerSupported?.();
  };

  return (
    <div
      className="rounded-xl border-2 border-[#C4A265]/40 bg-gradient-to-br from-[#FFFDF8] via-[#FAF5EC] to-[#F5EDE0] p-4 relative overflow-hidden"
      data-testid={`prayer-share-${m.id}`}
    >
      <div className="absolute top-0 left-0 w-1 h-full bg-[#C4A265]" />
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-1.5">
          <HeartStraight size={14} weight="fill" className="text-[#C4A265]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#C4A265] font-medium font-body">
            Someone asked for prayer
          </span>
        </div>
        <span className="text-[10px] text-[#B8A088] font-body">
          {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
        </span>
      </div>

      <p className="font-heading text-base text-[#2D1B0E] italic leading-relaxed mb-3" data-testid={`prayer-share-content-${m.id}`}>
        "{m.content}"
      </p>

      {(m.redactions_count > 0) && (
        <p className="text-[10px] text-[#8B7355] font-body italic mb-3 inline-flex items-center gap-1">
          <ShieldCheck size={11} weight="fill" className="text-[#C4A265]" />
          Anonymous · personal details stripped
        </p>
      )}

      {sent ? (
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-emerald-800 text-xs font-body inline-flex items-center gap-1.5" data-testid={`prayer-supported-${m.id}`}>
          <CheckCircle size={12} weight="fill" /> You prayed for this person.
        </div>
      ) : open ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {QUICK_PRAYER_PROMPTS.map((p) => (
              <button
                key={p}
                onClick={() => handleQuick(p)}
                data-testid={`quick-prayer-${m.id}`}
                className="text-[11px] font-body bg-white border border-[#E8DFD3] text-[#5C4033] px-3 py-1.5 rounded-full hover:bg-[#FAF5EC]"
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5">
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              maxLength={300}
              placeholder="Or write a short prayer…"
              data-testid={`custom-prayer-input-${m.id}`}
              className="flex-1 px-3 py-1.5 rounded-full bg-white border border-[#E8DFD3] text-xs font-body text-[#2D1B0E] focus:outline-none focus:border-[#C4A265]"
            />
            <button
              onClick={() => custom.trim() && handleQuick(custom.trim())}
              disabled={!custom.trim()}
              data-testid={`send-custom-prayer-${m.id}`}
              className="px-3 py-1.5 rounded-full bg-[#2D1B0E] text-white text-xs font-body font-medium disabled:opacity-50"
            >
              Pray
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          data-testid={`pray-for-this-${m.id}`}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white text-xs font-body font-medium"
        >
          <HandsPraying size={12} weight="duotone" /> Pray for this person
        </button>
      )}
    </div>
  );
}

